<script setup lang="ts">
import { Multiselect } from 'vue-multiselect';
import Permissions from '../../../shared/permissisons';
import TagsService from '../../pages/cms/modules/tags/_runtime/services/TagsService';

const emit = defineEmits<{
    (e: 'update:modelValue', value: string[]): void;
}>();

const props = withDefaults(defineProps<{
    modelValue: string[];
    canCreate?: boolean;
    locale?: string;
}>(), {
    canCreate: true,
    locale: ''
});

const { $log } = useLogging();
const { $alert } = useAlerts();
const { $t } = useLocalization();
const { $permissions } = usePermissions();

const canCreateTag = ref<boolean>(false);
const options = ref<Option[]>([]);

const tags = computed({
    get(): Option[]
    {
        return props.modelValue.map(p => ({
            text: p,
            value: p
        }));
    },
    set(value: Option[])
    {
        triggerModel(value);
    }
});

const placeholder = computed<string>(() =>
{
    return canCreateTag.value ? $t('[[[Wyszukaj albo dodaj nowy tag]]]') : $t('[[[Wyszukaj tag]]]');
});

function triggerModel(value: Option[]): void
{
    emit('update:modelValue', value.map(p => p.value));
}

function addTag(tag: string): void
{
    tag = tag.replace(/\s+/g, ' ').trim().toLowerCase().substring(0, 100);

    if (!props.modelValue.includes(tag))
    {
        const tagsList = tags.value.slice();

        tagsList.push({ text: tag, value: tag });
        tags.value = tagsList;
    }
}

const onSearchChanged = useDebounceFn(async (tagName: string): Promise<void> =>
{
    try
    {
        if (tagName && tagName.length > 0 && tagName.length <= 100)
        {
            options.value = (await TagsService.getAutcomplete(tagName, props.locale)).map(p => ({
                text: p,
                value: p
            }));
        }
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }
}, 500);

onBeforeMount(async (): Promise<void> =>
{
    canCreateTag.value = (await $permissions.all([Permissions.Cms.Tags.Edit]) && props.canCreate);
});
</script>

<template>
    <div class="d-flex flex-nowrap gap-1">
        <Multiselect
            v-model="tags"
            class="tag-multiselect"
            label="text"
            track-by="value"
            :options="options"
            :multiple="true"
            :taggable="canCreateTag"
            :placeholder="placeholder"
            :tag-placeholder="$t('[[[Dodaj nowy tag]]]')"
            :select-label="$t('[[[Naciśnij enter aby wybrać]]]')"
            :selected-label="$t('[[[Wybrano]]]')"
            :deselect-label="$t('[[[Naciśnij enter aby usunąć]]]')"
            :select-group-label="''"
            :deselect-group-label="''"
            @search-change="onSearchChanged"
            @tag="addTag"
        >
            <template #noOptions>
                {{ $t('[[[Lista jest pusta]]]') }}
            </template>
            <template #noResult>
                {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
            </template>
        </Multiselect>
    </div>
</template>

<style lang="scss" scoped>
.size {
    min-width: 64px !important;
    max-width: 64px !important;
}
</style>
